import { forwardRef } from 'react'
import type { MouseEvent } from 'react'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import { Box, IconButton } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { formatNotificationsLabel } from './notificationsText'

interface NotificationBellProps {
  count: number
  /** The notifications panel is open: the bell keeps the active colours. */
  open: boolean
  onClick: (event: MouseEvent<HTMLElement>) => void
  /** Placement in the header, e.g. a left margin. */
  sx?: SxProps<Theme>
}

const MAX_SHOWN_COUNT = 99

/** Header bell with the red unread counter at its top right; the counter is hidden at zero. */
const NotificationBell = forwardRef<HTMLButtonElement, NotificationBellProps>(function NotificationBell(
  { count, open, onClick, sx },
  ref
) {
  return (
    <IconButton
      ref={ref}
      aria-label={formatNotificationsLabel(count)}
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={onClick}
      sx={[{
        position: 'relative',
        width: 44,
        height: 44,
        borderRadius: 1,
        flexShrink: 0,
        color: open ? '#1a56c4' : '#5b6572',
        bgcolor: open ? '#e3ebfb' : 'transparent',
        '&:hover': { bgcolor: open ? '#e3ebfb' : 'action.hover' }
      }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <NotificationsNoneIcon sx={{ fontSize: 22 }} />
      {count > 0 && (
        <Box
          component="span"
          aria-hidden="true"
          data-testid="notification-bell-count"
          sx={{
            position: 'absolute',
            top: 4,
            left: 24,
            minWidth: 18,
            height: 18,
            padding: '0 5px',
            boxSizing: 'border-box',
            borderRadius: '999px',
            bgcolor: '#b42318',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #fff'
          }}
        >
          {count > MAX_SHOWN_COUNT ? `${MAX_SHOWN_COUNT}+` : count}
        </Box>
      )}
    </IconButton>
  )
})

export default NotificationBell
